import mapboxgl from '../mapboxClient';

class CompassRoseControl {
  private container: HTMLDivElement | null = null;
  private ring: SVGGElement | null = null;
  private label: SVGTextElement | null = null;
  private map: mapboxgl.Map | null = null;
  private rotateHandler: (() => void) | null = null;

  onAdd(mapInstance: mapboxgl.Map) {
    this.map = mapInstance;

    this.container = document.createElement('div');
    this.container.className = 'compass-rose-control mapboxgl-ctrl';
    this.container.title = 'Reset bearing to north';

    const SIZE = 32;
    const CX = 16;
    const CY = 16;
    // Bezel ring sits at r=14, ticks are short stubs just inside it
    const R_BG        = 15.5; // main dark fill
    const R_BEZEL     = 13.8; // inner bezel ring radius (ticks sit on this)
    const R_TICK_OUT  = 13.8; // outer end of tick (on the bezel)
    const R_TICK_MAJ  = 12.2; // inner end of major tick (~1.6px stub)
    const R_TICK_MIN  = 13.2; // inner end of minor tick (~0.6px stub)
    const R_NORTH_TIP  = 12.8;
    const R_NORTH_BASE = 9.2;
    const NORTH_HALF_ARC = 0.20;

    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('width', String(SIZE));
    svg.setAttribute('height', String(SIZE));
    svg.setAttribute('viewBox', `0 0 ${SIZE} ${SIZE}`);

    // Dark background disc
    const bg = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
    bg.setAttribute('cx', String(CX));
    bg.setAttribute('cy', String(CY));
    bg.setAttribute('r', String(R_BG));
    bg.setAttribute('fill', 'rgb(14,14,18)');
    svg.appendChild(bg);

    // Subtle bezel ring — slightly lighter band where the ticks sit
    const bezel = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
    bezel.setAttribute('cx', String(CX));
    bezel.setAttribute('cy', String(CY));
    bezel.setAttribute('r', String(R_BEZEL));
    bezel.setAttribute('fill', 'none');
    bezel.setAttribute('stroke', 'rgba(255,255,255,0.07)');
    bezel.setAttribute('stroke-width', '2.5');
    svg.appendChild(bezel);

    // Rotating ring — counter-rotates against map bearing so ticks stay fixed
    const ring = document.createElementNS('http://www.w3.org/2000/svg', 'g');
    ring.setAttribute('transform', `rotate(0,16,16)`);
    this.ring = ring;

    for (let i = 0; i < 16; i++) {
      const angleDeg = i * 22.5;
      const isMajor = i % 2 === 0;
      const isNorth = i === 0;
      const rad = (angleDeg - 90) * (Math.PI / 180);

      if (isNorth) {
        const tipX = CX + R_NORTH_TIP * Math.cos(rad);
        const tipY = CY + R_NORTH_TIP * Math.sin(rad);
        const b1x = CX + R_NORTH_BASE * Math.cos(rad + NORTH_HALF_ARC);
        const b1y = CY + R_NORTH_BASE * Math.sin(rad + NORTH_HALF_ARC);
        const b2x = CX + R_NORTH_BASE * Math.cos(rad - NORTH_HALF_ARC);
        const b2y = CY + R_NORTH_BASE * Math.sin(rad - NORTH_HALF_ARC);
        const tri = document.createElementNS('http://www.w3.org/2000/svg', 'polygon');
        tri.setAttribute('points', `${tipX},${tipY} ${b1x},${b1y} ${b2x},${b2y}`);
        tri.setAttribute('fill', '#e63946');
        ring.appendChild(tri);
      } else {
        const x1 = CX + R_TICK_OUT * Math.cos(rad);
        const y1 = CY + R_TICK_OUT * Math.sin(rad);
        const x2 = CX + (isMajor ? R_TICK_MAJ : R_TICK_MIN) * Math.cos(rad);
        const y2 = CY + (isMajor ? R_TICK_MAJ : R_TICK_MIN) * Math.sin(rad);
        const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
        line.setAttribute('x1', String(x1));
        line.setAttribute('y1', String(y1));
        line.setAttribute('x2', String(x2));
        line.setAttribute('y2', String(y2));
        line.setAttribute('stroke', isMajor ? 'rgba(255,255,255,0.68)' : 'rgba(255,255,255,0.28)');
        line.setAttribute('stroke-width', isMajor ? '1.5' : '0.9');
        line.setAttribute('stroke-linecap', 'round');
        ring.appendChild(line);
      }
    }

    svg.appendChild(ring);

    // Cardinal letter — centered, updates on rotate
    const label = document.createElementNS('http://www.w3.org/2000/svg', 'text');
    label.setAttribute('x', String(CX));
    label.setAttribute('y', String(CY));
    label.setAttribute('text-anchor', 'middle');
    label.setAttribute('dominant-baseline', 'central');
    label.setAttribute('fill', 'rgba(255,255,255,0.68)');
    label.setAttribute('font-size', '12');
    label.setAttribute('font-family', 'system-ui, -apple-system, BlinkMacSystemFont, sans-serif');
    label.setAttribute('font-weight', '700');
    label.textContent = 'N';
    this.label = label;
    svg.appendChild(label);

    this.container.appendChild(svg);

    this.container.addEventListener('click', () => {
      this.map?.easeTo({ bearing: 0, pitch: 0, duration: 350 });
    });

    this.rotateHandler = () => this._update();
    mapInstance.on('rotate', this.rotateHandler);
    this._update();

    return this.container;
  }

  private _bearingToCardinal(bearing: number): string {
    const b = ((bearing % 360) + 360) % 360;
    if (b < 22.5 || b >= 337.5) return 'N';
    if (b < 67.5) return 'NE';
    if (b < 112.5) return 'E';
    if (b < 157.5) return 'SE';
    if (b < 202.5) return 'S';
    if (b < 247.5) return 'SW';
    if (b < 292.5) return 'W';
    return 'NW';
  }

  private _update() {
    if (!this.map || !this.ring || !this.label) return;
    const bearing = this.map.getBearing();
    this.ring.setAttribute('transform', `rotate(${-bearing},16,16)`);
    const cardinal = this._bearingToCardinal(bearing);
    this.label.textContent = cardinal;
    this.label.setAttribute('font-size', cardinal.length > 1 ? '8' : '12');
  }

  onRemove() {
    if (this.map && this.rotateHandler) {
      this.map.off('rotate', this.rotateHandler);
    }
    this.container?.remove();
    this.container = null;
    this.map = null;
  }
}

export class MapNavigationControl {
  private container: HTMLDivElement | null = null;
  private compass = new CompassRoseControl();
  private zoom = new mapboxgl.NavigationControl({ showCompass: false, showZoom: true });

  onAdd(map: mapboxgl.Map) {
    this.container = document.createElement('div');
    this.container.className = 'custom-nav-stack mapboxgl-ctrl';
    this.container.append(this.compass.onAdd(map), this.zoom.onAdd(map));
    return this.container;
  }

  onRemove() {
    this.compass.onRemove();
    this.zoom.onRemove();
    this.container?.remove();
    this.container = null;
  }
}
